import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import type { Vacancy, Candidate } from '@/types';

const pipelineColumns = [
  { key: 'new', label: 'Новые', color: '' },
  { key: 'resume_scored', label: 'Резюме оценено', color: '' },
  { key: 'form_sent', label: 'Письмо отправлено', color: '' },
  { key: 'answers_received', label: 'Ответы получены', color: '' },
  { key: 'answers_scored', label: 'Анкета оценена', color: '' },
  { key: 'interview_scheduled', label: 'Интервью', color: '' },
  { key: 'rejected', label: 'Отказ', color: '' },
];

const statusBadgeColors: Record<string, string> = {
  new: 'bg-background-100 text-foreground-600',
  resume_scored: 'bg-purple-50 text-purple-700',
  form_sent: 'bg-secondary-100 text-secondary-700',
  answers_received: 'bg-accent-100 text-accent-700',
  answers_scored: 'bg-emerald-50 text-emerald-700',
  interview_scheduled: 'bg-amber-50 text-amber-700',
  rejected: 'bg-red-50 text-red-600',
};

export default function Pipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [selectedVacancyId, setSelectedVacancyId] = useState<string | null>(searchParams.get('vacancy'));
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<Vacancy[]>('/vacancies').then((data) => {
      setVacancies(data);
      // Вакансия из адреса могла остаться от другой организации — проверяем
      const valid = selectedVacancyId && data.some((v) => v.id === selectedVacancyId);
      if (!valid && data.length > 0) {
        const active = data.find((v) => v.status === 'active') || data[0];
        setSelectedVacancyId(active.id);
      }
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedVacancyId) return;
    setLoading(true);
    api.get<Candidate[]>(`/vacancies/${selectedVacancyId}/candidates`)
      .then(setCandidates)
      .catch(() => setCandidates([]))
      .finally(() => setLoading(false));
    const p = new URLSearchParams(searchParams);
    p.set('vacancy', selectedVacancyId);
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVacancyId]);

  const getCandidatesByStatus = (status: string) =>
    candidates.filter((c) => c.status === status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">Воронка подбора</h1>
          <p className="text-sm text-foreground-500 mt-1">Визуальное представление статусов кандидатов</p>
        </div>
        <select
          value={selectedVacancyId || ''}
          onChange={(e) => setSelectedVacancyId(e.target.value)}
          className="px-4 py-2.5 rounded-xl bg-white border border-background-200 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        >
          {vacancies.map((v) => (
            <option key={v.id} value={v.id}>{v.title}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-[40vh]">
          <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Kanban board */}
      {!loading && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {pipelineColumns.map((col) => {
            const colCandidates = getCandidatesByStatus(col.key);
            return (
              <div
                key={col.key}
                className="flex-shrink-0 w-[300px] bg-white rounded-xl border border-background-200"
              >
                {/* Column header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-800">{col.label}</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                      {colCandidates.length}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div className="p-3 space-y-2.5 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {colCandidates.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => navigate(`/candidates/${c.id}`)}
                      className="bg-white border border-background-200 rounded-lg p-3 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                          <span className="text-primary-700 font-heading font-semibold text-xs">{c.name.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                            {c.name}
                          </p>
                          <p className="text-xs text-foreground-400 truncate">{c.position}</p>
                        </div>
                      </div>

                      {(c.total_score || 0) > 0 && (
                        <div className="mt-2.5 flex items-center gap-2">
                          <div className="flex gap-1 flex-1">
                            <div className="h-1.5 rounded-full overflow-hidden flex-1 bg-purple-100">
                              <div className="h-full rounded-full bg-purple-500" style={{ width: `${c.resume_score || 0}%` }}></div>
                            </div>
                            <div className="h-1.5 rounded-full overflow-hidden flex-1 bg-emerald-100">
                              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${c.answers_score || 0}%` }}></div>
                            </div>
                            <div className="h-1.5 rounded-full overflow-hidden flex-1 bg-amber-100">
                              <div className="h-full rounded-full bg-amber-500" style={{ width: `${c.compatibility_score || 0}%` }}></div>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-foreground-700">{c.total_score}</span>
                        </div>
                      )}

                      <div className="mt-2 flex items-center gap-1.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusBadgeColors[c.status] || statusBadgeColors.new}`}>
                          {col.label}
                        </span>
                        {c.resume_source === 'hh.ru' && (
                          <span className="text-[10px] text-foreground-400">hh.ru</span>
                        )}
                        {c.resume_source === 'linkedin' && (
                          <span className="text-[10px] text-foreground-400">LinkedIn</span>
                        )}
                        {c.replied && (
                          <span className="text-[10px] text-accent-600 font-medium">ответил</span>
                        )}
                      </div>
                    </div>
                  ))}

                  {colCandidates.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center mb-2">
                        <i className="ri-inbox-line text-sm text-foreground-300"></i>
                      </div>
                      <p className="text-xs text-foreground-400">Нет кандидатов</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
